import { useState, useEffect, useCallback } from "react";
import { Bell, BellRing, Settings, X, Clock, User, Volume2, VolumeX, Check, Trash2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useNotifications } from "../hooks/useNotifications";
import { api } from "../App";

export default function NotificationBell() {
  const { permission, requestPermission, settings, updateSettings, upcoming } = useNotifications();
  const [showPanel, setShowPanel] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [pendingPatients, setPendingPatients] = useState([]);

  const fetchPending = useCallback(async () => {
    try {
      const res = await api.get("/patients/pending");
      setPendingPatients(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, [fetchPending]);

  const totalCount = upcoming.length + pendingPatients.length;

  const handleApprove = async (pendingId) => {
    try {
      await api.post(`/patients/approve/${pendingId}`);
      setPendingPatients(prev => prev.filter(p => p.pending_id !== pendingId));
    } catch (err) {
      console.error("Approve error:", err);
    }
  };

  const handleReject = async (pendingId) => {
    try {
      await api.delete(`/patients/reject/${pendingId}`);
      setPendingPatients(prev => prev.filter(p => p.pending_id !== pendingId));
    } catch (err) {
      console.error("Reject error:", err);
    }
  };

  const typeLabels = {
    consultation: "Consulta", cleaning: "Limpeza", treatment: "Tratamento",
    surgery: "Cirurgia", checkup: "Revisão", emergency: "Emergência",
  };
  const typeColors = {
    consultation: "bg-teal-100 text-teal-700", cleaning: "bg-blue-100 text-blue-700",
    treatment: "bg-purple-100 text-purple-700", surgery: "bg-red-100 text-red-700",
    checkup: "bg-amber-100 text-amber-700", emergency: "bg-rose-100 text-rose-700",
  };

  return (
    <div className="relative">
      <button
        onClick={() => { setShowPanel(!showPanel); setShowSettings(false); }}
        className={`relative p-2 rounded-xl transition-all ${
          totalCount > 0
            ? "bg-teal-50 text-teal-700 hover:bg-teal-100"
            : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
        }`}
        data-testid="notification-bell"
      >
        {totalCount > 0 ? <BellRing className="w-5 h-5 animate-pulse" /> : <Bell className="w-5 h-5" />}
        {totalCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
            {totalCount}
          </span>
        )}
      </button>

      {showPanel && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setShowPanel(false)} />
          <div className="absolute left-0 top-12 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-40 overflow-hidden" data-testid="notification-panel">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800 text-sm">Notificações</h3>
              <div className="flex items-center gap-1">
                <button onClick={() => setShowSettings(!showSettings)} className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors" data-testid="notification-settings-btn">
                  <Settings className="w-4 h-4 text-slate-500" />
                </button>
                <button onClick={() => setShowPanel(false)} className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors">
                  <X className="w-4 h-4 text-slate-500" />
                </button>
              </div>
            </div>

            {showSettings ? (
              <div className="p-4 space-y-4" data-testid="notification-settings-panel">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Ativar notificações</span>
                  </div>
                  <Switch checked={settings.enabled} onCheckedChange={handleEnable} data-testid="notification-enabled-switch" />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Antecedência</span>
                  </div>
                  <select
                    value={settings.advance_minutes}
                    onChange={(e) => updateSettings({ advance_minutes: parseInt(e.target.value) })}
                    className="h-8 rounded-md border border-input bg-background px-2 text-sm w-28"
                    data-testid="advance-minutes-select"
                  >
                    <option value={5}>5 min</option>
                    <option value={10}>10 min</option>
                    <option value={15}>15 min</option>
                    <option value={30}>30 min</option>
                    <option value={60}>1 hora</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {settings.sound ? <Volume2 className="w-4 h-4 text-slate-500" /> : <VolumeX className="w-4 h-4 text-slate-500" />}
                    <span className="text-sm text-slate-700">Som</span>
                  </div>
                  <Switch checked={settings.sound} onCheckedChange={(v) => updateSettings({ sound: v })} data-testid="notification-sound-switch" />
                </div>
                {permission !== "granted" && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700 mb-2">Permissão de notificação não concedida.</p>
                    <Button size="sm" variant="outline" onClick={requestPermission} className="w-full text-xs" data-testid="request-permission-btn">Permitir notificações</Button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Tabs */}
                {pendingPatients.length > 0 && (
                  <div className="flex border-b border-slate-200">
                    <button
                      onClick={() => setActiveTab("all")}
                      className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${activeTab === "all" ? "text-teal-700 border-b-2 border-teal-600" : "text-slate-500 hover:text-slate-700"}`}
                    >
                      Consultas {upcoming.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 text-[10px]">{upcoming.length}</span>}
                    </button>
                    <button
                      onClick={() => setActiveTab("patients")}
                      className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${activeTab === "patients" ? "text-teal-700 border-b-2 border-teal-600" : "text-slate-500 hover:text-slate-700"}`}
                      data-testid="pending-patients-tab"
                    >
                      Pacientes <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px]">{pendingPatients.length}</span>
                    </button>
                  </div>
                )}

                <div className="max-h-72 overflow-y-auto">
                  {/* Upcoming Appointments */}
                  {(activeTab === "all" || pendingPatients.length === 0) && (
                    <>
                      {upcoming.length === 0 && pendingPatients.length === 0 ? (
                        <div className="p-6 text-center">
                          <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-sm text-slate-500">Nenhuma notificação</p>
                          <p className="text-xs text-slate-400 mt-1">Consultas próximas e cadastros aparecerão aqui</p>
                        </div>
                      ) : upcoming.length === 0 && activeTab === "all" ? (
                        <div className="p-4 text-center">
                          <p className="text-sm text-slate-400">Nenhuma consulta próxima</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {upcoming.map((apt) => (
                            <div key={apt.appointment_id} className="px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`upcoming-${apt.appointment_id}`}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <Clock className="w-3.5 h-3.5 text-teal-600" />
                                  <span className="text-sm font-semibold text-slate-800">{apt.time}</span>
                                </div>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${typeColors[apt.type] || "bg-slate-100 text-slate-600"}`}>
                                  {typeLabels[apt.type] || apt.type}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 ml-5">
                                <User className="w-3 h-3 text-slate-400" />
                                <span className="text-xs text-slate-600">{apt.patient_name || "Paciente"}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Pending Patients */}
                  {activeTab === "patients" && (
                    <div className="divide-y divide-slate-100">
                      {pendingPatients.map((p) => (
                        <div key={p.pending_id} className="px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`pending-${p.pending_id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-2.5 min-w-0 flex-1">
                              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <UserPlus className="w-4 h-4 text-amber-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                                <p className="text-xs text-slate-500 truncate">{p.phone || p.email || "Sem contato"}</p>
                                <p className="text-[10px] text-slate-400 mt-0.5">
                                  {new Date(p.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                              <button
                                onClick={() => handleApprove(p.pending_id)}
                                className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                                title="Aprovar"
                                data-testid={`approve-${p.pending_id}`}
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleReject(p.pending_id)}
                                className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                                title="Rejeitar"
                                data-testid={`reject-${p.pending_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );

  function handleEnable() {
    if (permission !== "granted") {
      requestPermission().then((result) => {
        if (result === "granted") updateSettings({ enabled: true });
      });
    } else {
      updateSettings({ enabled: !settings.enabled });
    }
  }
}
